import { Injectable, Resource } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Enrollment, EnrollmentFormData, CandidateEnrollment, Boleto, InscricaoCota, InscricaoCondicaoEspecial } from '../models/enrollment.model';
import { ApiResponse } from '../models/api-response.model';
import { Exemption, ExemptionResponse, ResourceRequest, ResourceResponse, ResourceResponseList, ResourceTypeResponse } from '@/models';
import { InscricaoTitulo, InscricaoTituloResponse, EditalTitulo } from '@/models/titulo.model';

export interface InscricaoRequest {
  cargoId: number;
  /** Obrigatório quando o cargo/edital tiver mais de uma cidade de realização */
  cidadeId?: number;
  escolaPublica?: boolean;
  candidatoComCota?: boolean;
  tipoCotaId?: number;
  candidatoComCondicaoEspecial?: boolean;
  tipoCondicaoEspecialId?: number;
  etapaIsencaoIds?: number[];
}

export interface InscricaoDetalhada {
  id: number;
  numeroInscricao: string;
  cotas?: { id: number; tipoCotaId?: number }[];
  condicoesEspeciais?: { id: number; tipoCondicaoEspecialId?: number }[];
  isencoes?: { id: number; etapaIsencaoId?: number; tipoIsencaoId?: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class EnrollmentService {
  private apiUrl = `${environment.apiUrl}/candidatos/inscricoes`;

  constructor(private http: HttpClient) {}

  /**
   * Apply for a position (create enrollment)
   * Envia apenas dados como JSON (application/json), sem arquivos
   */
  applyForPosition(data: InscricaoRequest): Observable<any> {
    // Construir objeto JSON para enviar
    const requestBody: any = {
      cargoId: data.cargoId
    };

    if (data.cidadeId !== undefined) {
      requestBody.cidadeId = data.cidadeId;
    }
    
    if (data.escolaPublica !== undefined) {
      requestBody.escolaPublica = data.escolaPublica;
    }
    
    if (data.candidatoComCota !== undefined) {
      requestBody.candidatoComCota = data.candidatoComCota;
    }
    
    if (data.tipoCotaId) {
      requestBody.tipoCotaId = data.tipoCotaId;
    }
    
    if (data.candidatoComCondicaoEspecial !== undefined) {
      requestBody.candidatoComCondicaoEspecial = data.candidatoComCondicaoEspecial;
    }
    
    if (data.tipoCondicaoEspecialId) {
      requestBody.tipoCondicaoEspecialId = data.tipoCondicaoEspecialId;
    }
    
    // Adicionar etapaIsencaoIds como array
    if (data.etapaIsencaoIds && data.etapaIsencaoIds.length > 0) {
      requestBody.etapaIsencaoIds = data.etapaIsencaoIds;
    }

    return this.http.post(`${this.apiUrl}`, requestBody);
  }

  /**
   * Get enrollment details by ID
   * Retorna detalhes da inscrição incluindo IDs das solicitações (cotas, condições especiais, isenções)
   */
  getEnrollmentDetails(inscricaoId: number): Observable<InscricaoDetalhada> {
    return this.http.get<InscricaoDetalhada>(`${this.apiUrl}/${inscricaoId}`);
  }

  /**
   * Upload documento de cota
   * POST /api/candidatos/inscricoes/{inscricaoId}/cotas/{inscricaoCotaId}/documento
   */
  uploadDocumentoCota(inscricaoId: number, inscricaoCotaId: number, documento: File): Observable<void> {
    const formData = new FormData();
    formData.append('documento', documento);
    return this.http.post<void>(`${this.apiUrl}/${inscricaoId}/cotas/${inscricaoCotaId}/documento`, formData);
  }

  /**
   * Upload documento de condição especial
   * POST /api/candidatos/inscricoes/{inscricaoId}/condicoes-especiais/{inscricaoCondicaoEspecialId}/documento
   */
  uploadDocumentoCondicaoEspecial(inscricaoId: number, inscricaoCondicaoEspecialId: number, documento: File): Observable<void> {
    const formData = new FormData();
    formData.append('documento', documento);
    return this.http.post<void>(`${this.apiUrl}/${inscricaoId}/condicoes-especiais/${inscricaoCondicaoEspecialId}/documento`, formData);
  }

  /**
   * Upload documento de isenção
   * POST /api/candidatos/inscricoes/{inscricaoId}/isencoes/{inscricaoIsencaoId}/documento
   */
  uploadDocumentoIsencao(inscricaoId: number, inscricaoIsencaoId: number, documento: File): Observable<void> {
    const formData = new FormData();
    formData.append('documento', documento);
    return this.http.post<void>(`${this.apiUrl}/${inscricaoId}/isencoes/${inscricaoIsencaoId}/documento`, formData);
  }

  /**
   * Upload documento geral
   * POST /api/candidatos/inscricoes/{inscricaoId}/documentos/{tipoDocumentoId}
   */
  uploadDocumentoGeral(inscricaoId: number, tipoDocumentoId: number, documento: File): Observable<void> {
    const formData = new FormData();
    formData.append('documento', documento);
    return this.http.post<void>(`${this.apiUrl}/${inscricaoId}/documentos/${tipoDocumentoId}`, formData);
  }

  /**
   * Get enrollment form data for a specific contest
   */
  getEnrollmentFormData(editalId: number): Observable<EnrollmentFormData> {
    return this.http.get<EnrollmentFormData>(`${this.apiUrl}/form-data/${editalId}`);
  }

  /**
   * Get all enrollments for the current candidate
   */
  getCandidateEnrollments(): Observable<CandidateEnrollment[]> {
    return this.http.get<CandidateEnrollment[]>(`${this.apiUrl}`);
  }

  /**
   * Generate payment slip (boleto) for an enrollment
   */
  generateBoleto(inscricaoId: number): Observable<Boleto> {
    return this.http.post<Boleto>(`${this.apiUrl}/${inscricaoId}/boletos`, {});
  }

  /**
   * Get payment slip (boleto) by ID
   */
  getBoleto(boletoId: number): Observable<Boleto> {
    return this.http.get<Boleto>(`${environment.apiUrl}/candidatos/boletos/${boletoId}`);
  }

  /**
   * Get enrollment receipt (comprovante) by enrollment ID
   */
  getReceipt(inscricaoId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${inscricaoId}/comprovante`);
  }

  /**
   * Sync payment status for a boleto
   */
  syncBoleto(boletoId: number): Observable<Boleto> {
    return this.http.post<Boleto>(`${environment.apiUrl}/candidatos/boletos/${boletoId}/sync`, {});
  }

  /**
   * Get quotas requested for an enrollment
   */
  getEnrollmentQuotas(inscricaoId: number): Observable<InscricaoCota[]> {
    return this.http.get<InscricaoCota[]>(`${this.apiUrl}/${inscricaoId}/cotas`);
  }

  /**
   * Get special conditions requested for an enrollment
   */
  getEnrollmentSpecialConditions(inscricaoId: number): Observable<InscricaoCondicaoEspecial[]> {
    return this.http.get<InscricaoCondicaoEspecial[]>(`${this.apiUrl}/${inscricaoId}/condicoes-especiais`);
  }
  
  /**
   * Get exemptions requested for an enrollment
   */
  getEnrollmentExemptions(inscricaoId: number): Observable<ExemptionResponse[]> {
    return this.http.get<ExemptionResponse[]>(`${this.apiUrl}/${inscricaoId}/isencao`);
  }

  /**
   * Get resources requested for an enrollment
   */
  getEnrollmentResources(inscricaoId: number): Observable<ResourceResponseList> {
    return this.http.get<ResourceResponseList>(`${this.apiUrl}/${inscricaoId}/recurso`);
  }

  /**
   * Request a resource for an enrollment (legacy - using ResourceRequest)
   */
  requestResource(inscricaoId: number, data: ResourceRequest): Observable<ResourceResponse> {
    return this.http.post<ResourceResponse>(`${this.apiUrl}/${inscricaoId}/recurso`, data);
  }

  /**
   * Request a resource for an enrollment (using FormData with multiple documents)
   * According to API documentation: 
   * - textoRecurso (obrigatório)
   * - tipoRecursoId (recomendado) - o sistema busca automaticamente a etapa ativa
   * - etapaRecursoId (opcional) - não usado quando tipoRecursoId é fornecido
   * - documento_recurso_{tipoDocumentoId} files
   */
  requestResourceFormData(
    inscricaoId: number,
    tipoRecursoId: number,
    textoRecurso: string,
    documentosPorTipo: Map<number, File>
  ): Observable<ResourceResponse> {
    const formData = new FormData();
    
    // Campo obrigatório
    formData.append('textoRecurso', textoRecurso);
    
    // Usar apenas tipoRecursoId (recomendado pela documentação)
    // O sistema busca automaticamente a etapa ativa baseada no tipoRecursoId
    formData.append('tipoRecursoId', tipoRecursoId.toString());

    // Adicionar documentos no formato: documento_recurso_{tipoDocumentoId}
    documentosPorTipo.forEach((file, tipoDocumentoId) => {
      formData.append(`documento_recurso_${tipoDocumentoId}`, file);
    });

    return this.http.post<ResourceResponse>(
      `${this.apiUrl}/${inscricaoId}/recurso`, 
      formData
    );
  }

  /**
   * Get resource types available for an enrollment
   * GET /{inscricaoId}/tipos-recursos
   * Returns only resource types that are active and within the resource stage period
   */
  getResourceTypes(inscricaoId: number): Observable<ResourceTypeResponse[]> {
    return this.http.get<ResourceTypeResponse[]>(`${this.apiUrl}/${inscricaoId}/tipos-recursos`);
  }

  /**
   * Get titles sent for an enrollment
   * GET /api/candidatos/inscricoes/{inscricaoId}/titulos
   * Returns: { titulos: InscricaoTitulo[], etapaTitulo: EtapaTitulo | null }
   */
  getEnrollmentTitulos(inscricaoId: number): Observable<InscricaoTituloResponse> {
    return this.http.get<InscricaoTituloResponse>(`${this.apiUrl}/${inscricaoId}/titulos`);
  }

  /**
   * Get a specific title by ID
   * GET /api/candidatos/inscricoes/{inscricaoId}/titulos/{id}
   */
  getEnrollmentTituloById(inscricaoId: number, id: number): Observable<InscricaoTitulo> {
    return this.http.get<InscricaoTitulo>(`${this.apiUrl}/${inscricaoId}/titulos/${id}`);
  }
  
  /**
   * Send a title document
   * POST /api/candidatos/inscricoes/{inscricaoId}/titulos
   * Content-Type: multipart/form-data
   * Form Data:
   * - editalTituloId: number
   * - documento_titulo_{editalTituloId}: File (PDF)
   */
  sendTitulo(inscricaoId: number, editalTituloId: number, file: File): Observable<InscricaoTitulo> {
    const formData = new FormData();
    formData.append('editalTituloId', editalTituloId.toString());
    formData.append(`documento_titulo_${editalTituloId}`, file);
    return this.http.post<InscricaoTitulo>(`${this.apiUrl}/${inscricaoId}/titulos`, formData);
  }

  /**
   * Remove a sent title
   * DELETE /api/candidatos/inscricoes/{inscricaoId}/titulos/{id}
   */
  removeTitulo(inscricaoId: number, tituloId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${inscricaoId}/titulos/${tituloId}`);
  }

  /**
   * Get available title types for an enrollment
   * GET /api/candidatos/inscricoes/{inscricaoId}/titulos/tipos-titulos
   */
  getTitulosDisponiveis(inscricaoId: number): Observable<EditalTitulo[]> {
    return this.http.get<EditalTitulo[]>(`${this.apiUrl}/${inscricaoId}/titulos/tipos-titulos`);
  }
}
